import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  Assignment as TaskIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

interface Employee {
  id: number;
  employee_number?: string;
  first_name: string;
  last_name: string;
  email: string;
  department: string;
  job_title: string;
  annual_salary?: number;
  hourly_rate?: number;
}

interface TerminateEmployeeDialogProps {
  open: boolean;
  employee: Employee | null;
  onClose: () => void;
  onConfirm: (terminationData: any) => void;
}

const TerminateEmployeeDialog: React.FC<TerminateEmployeeDialogProps> = ({
  open,
  employee,
  onClose,
  onConfirm
}) => {
  const [terminationData, setTerminationData] = useState({
    termination_date: new Date().toISOString().split('T')[0],
    termination_reason: '',
    notice_period_served: true,
    final_pay_date: '',
    holiday_pay_owed: '',
    notice_pay: '',
    redundancy_pay: '',
    other_payments: '',
    p45_required: true,
    return_equipment: false,
    return_access_cards: false,
    return_company_property: false,
    exit_interview_completed: false,
    handover_completed: false,
    notes: ''
  });

  const [checklist, setChecklist] = useState({
    equipment_returned: false,
    access_revoked: false,
    final_pay_calculated: false,
    p45_prepared: false,
    exit_interview: false,
    handover_complete: false
  });

  if (!employee) return null;

  const handleInputChange = (field: string, value: any) => {
    setTerminationData(prev => ({ ...prev, [field]: value }));
  };

  const handleChecklistChange = (field: string, checked: boolean) => {
    setChecklist(prev => ({ ...prev, [field]: checked }));
  };

  const handleConfirm = () => {
    const finalData = {
      ...terminationData,
      checklist,
      employee_id: employee.id
    };
    onConfirm(finalData);
    onClose();
  };

  const calculateFinalPay = () => {
    // Simplified calculation - in real system this would be more complex
    const basePay = employee.annual_salary ? employee.annual_salary / 12 : (employee.hourly_rate || 0) * 160;
    return basePay;
  };

  const isFormValid = () => {
    return terminationData.termination_date && 
           terminationData.termination_reason && 
           terminationData.final_pay_date;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Terminate Employment
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            ⚠️ Important: Employment Termination
          </Typography>
          <Typography variant="body2">
            This action will terminate the employment of <strong>{employee.first_name} {employee.last_name}</strong>. 
            Please ensure all required steps are completed and documentation is in order.
          </Typography>
        </Alert>

        {/* Employee Summary */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon color="primary" />
              Employee Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Name</Typography>
                <Typography variant="body2">{employee.first_name} {employee.last_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Employee Number</Typography>
                <Typography variant="body2">{employee.employee_number}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Department</Typography>
                <Typography variant="body2">{employee.department}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Job Title</Typography>
                <Typography variant="body2">{employee.job_title}</Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Termination Details */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Termination Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Termination Date *"
                  type="date"
                  value={terminationData.termination_date}
                  onChange={(e) => handleInputChange('termination_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Termination Reason *</InputLabel>
                  <Select
                    value={terminationData.termination_reason}
                    onChange={(e) => handleInputChange('termination_reason', e.target.value)}
                  >
                    <MenuItem value="resignation">Resignation</MenuItem>
                    <MenuItem value="redundancy">Redundancy</MenuItem>
                    <MenuItem value="dismissal">Dismissal</MenuItem>
                    <MenuItem value="mutual_agreement">Mutual Agreement</MenuItem>
                    <MenuItem value="end_of_contract">End of Contract</MenuItem>
                    <MenuItem value="retirement">Retirement</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={terminationData.notice_period_served}
                      onChange={(e) => handleInputChange('notice_period_served', e.target.checked)}
                    />
                  }
                  label="Notice period served in full"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Final Pay Calculation */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PaymentIcon color="primary" />
              Final Pay Calculation
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Final Pay Date *"
                  type="date"
                  value={terminationData.final_pay_date}
                  onChange={(e) => handleInputChange('final_pay_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Holiday Pay Owed ($)"
                  type="number"
                  value={terminationData.holiday_pay_owed}
                  onChange={(e) => handleInputChange('holiday_pay_owed', e.target.value)}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Notice Pay ($)"
                  type="number"
                  value={terminationData.notice_pay}
                  onChange={(e) => handleInputChange('notice_pay', e.target.value)}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Redundancy Pay ($)"
                  type="number"
                  value={terminationData.redundancy_pay}
                  onChange={(e) => handleInputChange('redundancy_pay', e.target.value)}
                  placeholder="0.00"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Other Payments ($)"
                  type="number"
                  value={terminationData.other_payments}
                  onChange={(e) => handleInputChange('other_payments', e.target.value)}
                  placeholder="0.00"
                  helperText="Bonus, commission, expenses, etc."
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">US Employment Law Reminder</Typography>
              <Typography variant="body2">
                • Statutory notice periods apply unless contractual notice is longer<br/>
                • PTO payout must be calculated for unused leave (per state law)<br/>
                • Severance pay may be due if applicable<br/>
                • W-2 must be issued by January 31
              </Typography>
            </Alert>
          </CardContent>
        </Card>

        {/* Termination Checklist */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TaskIcon color="primary" />
              Termination Checklist
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.equipment_returned}
                    onChange={(e) => handleChecklistChange('equipment_returned', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="Equipment Returned" 
                  secondary="Laptop, phone, keys, uniform, etc."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.access_revoked}
                    onChange={(e) => handleChecklistChange('access_revoked', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="Access Revoked" 
                  secondary="IT systems, building access, email accounts"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.final_pay_calculated}
                    onChange={(e) => handleChecklistChange('final_pay_calculated', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="Final Pay Calculated" 
                  secondary="Including holiday pay, notice pay, and deductions"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.p45_prepared}
                    onChange={(e) => handleChecklistChange('p45_prepared', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="W-2 Prepared" 
                  secondary="Tax form for employee's records"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.exit_interview}
                    onChange={(e) => handleChecklistChange('exit_interview', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="Exit Interview Completed" 
                  secondary="Feedback and improvement opportunities"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Checkbox
                    checked={checklist.handover_complete}
                    onChange={(e) => handleChecklistChange('handover_complete', e.target.checked)}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary="Handover Complete" 
                  secondary="Knowledge transfer and project handover"
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Additional Notes</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={terminationData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder="Any additional information about the termination..."
            />
          </CardContent>
        </Card>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          color="warning"
          onClick={handleConfirm}
          disabled={!isFormValid()}
        >
          Confirm Termination
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TerminateEmployeeDialog;
